import { HttpClient, HttpParams } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { EnvironmentsHelperService } from '@environments/core/services/environments-helper.service'
import { LongLoadingTimeframes } from '@modules/trading-view-module/constants/long-loading-timeframes'
import { TradingViewTimeframesEnum } from '@modules/trading-view-module/enums/trading-view-timeframes.enum'
import { TickerRefPolygonResponseInterface } from '@modules/trading-view-module/interfaces/ticker-ref-polygon-response.interface'
import { Observable, shareReplay } from 'rxjs'
import { AssetExanteInterface } from '@modules/stocks-module/general/interfaces/asset-exante.interface'
import { AssetShortInterface } from '@modules/stocks-module/general/interfaces/asset-short.interface'
import { SearchAssetsPaginationInterface } from '@modules/stocks-module/general/interfaces/search-assets-pagination.interface'
import { TickerPolygonInterface } from '@modules/stocks-module/general/interfaces/ticker-polygon.interface'
import { LatestPricesResponseInterface } from '@modules/stocks-module/general/interfaces/latest-prices-response.interface'

@Injectable({
	providedIn: 'root',
})
export class StocksService {
	protected readonly longLoadingTimeframes: TradingViewTimeframesEnum[] = LongLoadingTimeframes
	protected readonly boolean: BooleanConstructor = Boolean

	private readonly _http: HttpClient = inject<HttpClient>(HttpClient)
	private readonly _environmentsHelperService: EnvironmentsHelperService =
		inject<EnvironmentsHelperService>(EnvironmentsHelperService)

	private _activeAssetShort$: Map<string, Observable<AssetShortInterface>> = new Map<
		string,
		Observable<AssetShortInterface>
	>()

	public getExanteAsset(symbol: string): Observable<AssetExanteInterface> {
		const params: HttpParams = new HttpParams().append('symbol', symbol.toUpperCase())

		return this._http.get<AssetExanteInterface>(
			`${this._environmentsHelperService.apiUrl}/alpaca/get_exante_asset/`,
			{
				params,
			},
		)
	}

	public getAssetShort(symbol: string): Observable<AssetShortInterface> {
		let activeAssetShort$: Observable<AssetShortInterface> | undefined = this._activeAssetShort$.get(symbol)

		if (!activeAssetShort$) {
			activeAssetShort$ = this._http
				.get<AssetShortInterface>(
					`${this._environmentsHelperService.apiUrl}/alpaca/get_asset_by_sym_short/${symbol}`,
				)
				.pipe(shareReplay({ bufferSize: 1, refCount: true }))
			this._activeAssetShort$.set(symbol, activeAssetShort$)
		}

		return activeAssetShort$
	}

	public searchAssets(value: string, enableOtc: boolean = false): Observable<SearchAssetsPaginationInterface> {
		const params: HttpParams = new HttpParams().append('query', value).append('otc_enable_switcher', enableOtc)
		return this._http.get<SearchAssetsPaginationInterface>(
			`${this._environmentsHelperService.apiUrl}/assets/all_assets/`,
			{ params },
		)
	}

	public getTickerPolygon(symbol: string): Observable<TickerPolygonInterface> {
		const params: HttpParams = new HttpParams().append('symbol', symbol)

		return this._http.get<TickerPolygonInterface>(
			`${this._environmentsHelperService.apiUrl}/proxy_api/v1/polygon/ticker_currency/`,
			{
				params,
			},
		)
	}

	public searchTickersReferences(
		userInput: string,
		searchType: 'ticker' | 'search',
	): Observable<TickerRefPolygonResponseInterface> {
		const params: HttpParams = new HttpParams()
			.append(searchType, `${userInput.toUpperCase()}`)
			.append('active', true)
			.append('limit', 100)

		return this._http.get<TickerRefPolygonResponseInterface>(
			`${this._environmentsHelperService.apiUrl}/proxy_api/v1/polygon/ticker_reference/?${params}`,
		)
	}

	public getTickerHistory(
		symbol: string,
		timePeriod: string,
		timeframe?: TradingViewTimeframesEnum,
	): Observable<LatestPricesResponseInterface> {
		let params: HttpParams = new HttpParams()
			.set('adjusted', 'true')
			.set('sort', 'desc')
			.set('ticker', symbol)
			.set('timePeriod', timePeriod)

		if (timeframe && this.longLoadingTimeframes.includes(timeframe)) {
			params = params.set('limit', '50000')
		}

		return this._http.get<LatestPricesResponseInterface>(
			`${this._environmentsHelperService.apiUrl}/proxy_api/v1/polygon/ticker_history`,
			{ params },
		)
	}
}
